<template>
  <div>
    <template v-if="readOnly">
      <div class="verdi" :class="{ 'right-align': singleLine }">
        <template v-if="modelValue !== null && modelValue !== undefined && userPreferences">
          {{ formattedModelValue }}
        </template>
        <template v-else>
          <span class="verdi muted">(blank)</span>
        </template>
      </div>
    </template>
    <template v-else>
      <div class="input-number-container">
        <div class="input-wrapper">
          <BaseInput :clearable="header.isNullable" :error="hasError" :error-message="errorMessage" :model-value="formattedModelValue"
                     type="text" @change="changed"/>
        </div>
      </div>
      <BaseProgressBar v-if="saving" />
      <div v-else style="height: 4px;padding: 0;margin: 0;" />
    </template>
  </div>
</template>

<script setup>
import { onMounted, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useTableStore } from '@/store/storeTable'
import { useUserPreferencesStore } from "@/store/storeUserPreferences.js";
import { formatDate, formatDateOnly, parseDate } from "@/helpers/DateFormatter.ts";

import BaseInput from "@/components/BaseComponents/BaseInput.vue";
import BaseProgressBar from "@/components/BaseComponents/BaseProgressBar.vue";

const userPreferencesStore = useUserPreferencesStore();
const { userPreferences } = storeToRefs(userPreferencesStore);

const props = defineProps({
  modelValue: {
    type: [String, Date],
    default: null
  },
  readOnly: {
    type: Boolean,
    default: false
  },
  singleLine: {
    type: Boolean,
    default: false
  },
  autoSave: {
    type: Boolean,
    default: true
  },
  rowId: { type: [String, Number], default: '' },
  header: { type: Object, required: true }
});

const emit = defineEmits(['update:modelValue'])
const hasError = ref(false)
const errorMessage = ref('')
const saving = ref(false)

const tableStore = useTableStore()
const formattedModelValue = ref(props.modelValue);

// run formatting on parameter, update formattedModelValue
const runFormatting = (newValue) => {
  const preferredDateFormat = 'DMY'; // default format

  // fetch preferredDateFormat from user preferences

  let valueToFormat = newValue;
  if (!(newValue instanceof Date))
    valueToFormat = parseDate(newValue, preferredDateFormat);

  if (isDateTimeField()) {
    formattedModelValue.value = formatDate(valueToFormat);
  } else {
    formattedModelValue.value = formatDateOnly(valueToFormat);
  }
}

watch(() => props.modelValue, async (newValue) => {
  runFormatting(newValue);
});

onMounted(async () => {
  // make sure we have user preferences available
  await userPreferencesStore.getUserPreferences();
  runFormatting(props.modelValue);
});

function isDateTimeField () {
  return props.header.basicDataType === 'datetime2';
}

function parseTime (input) {
  let hour, minute, second, millisecond, secondPart;
  hour = minute = second = millisecond = secondPart = 0;

  if (input) {
    [hour, minute, secondPart] = input.split(':');

    if (secondPart) // seconds might not be specified, allow user to only use HH:MM
      [second, millisecond] = secondPart.split('.');

    if (!millisecond) // set millisecond to zero when only seconds has been specified
      millisecond = 0;

    if (hour > 23 || hour < 0)
      hour = undefined;

  }
  return [hour, minute, second, millisecond];
}

// parse raw input from text box. split value into date and time part,
// parse them separately and combine into a Date object
function parseInput (input) {
  const inputTrimmed = input.trim();

  // accept ' '  ', ' and 'T' as time separators
  // e.g.
  // 12.04.2024 12:34:23.123
  // 12.04.2024, 12:34:23.123
  // 12.04.2024T12:34:23.123
  let splitSeparator = ', ';
  if (inputTrimmed.indexOf(', ') === -1) {
    if (inputTrimmed.indexOf('T') !== -1)
      splitSeparator = 'T';
    else
      splitSeparator = ' ';
  }

  const [datePart, timePart] = inputTrimmed.split(splitSeparator);
  const modelValueAsDate = parseDate(datePart, 'DMY');

  const [hour, minute, second, millisecond] = parseTime(timePart);
  modelValueAsDate.setUTCHours(hour);
  modelValueAsDate.setUTCMinutes(minute);
  modelValueAsDate.setUTCSeconds(second);
  modelValueAsDate.setUTCMilliseconds(millisecond);

  return modelValueAsDate;
}

async function changed (newValue) {
  if (newValue === '' || newValue === null) {
    if (props.header.isNullable) {
      emit('update:modelValue', null)
      if (!props.autoSave) return;
      const item = { rowId: props.rowId, databaseObjectId: props.header.databaseObjectId, columnName: props.header.field, oldValue: props.modelValue, newValue: null };
      hasError.value = false
      saving.value = true
      await tableStore.saveData(item);
      saving.value = false;
      return;
    } else {
      hasError.value = true;
      errorMessage.value = 'Feltet kan ikke være tomt';
      return;
    }
  }
  const modelValueAsDate = parseInput(newValue)

  // isNaN is used to fetch 'Invalid Date' states in the Date object
  if (!modelValueAsDate || isNaN(modelValueAsDate.getTime())) {
    hasError.value = true
    errorMessage.value = `Ugyldig datoformat.`;
    console.error("Invalid date value", newValue)
    formattedModelValue.value = newValue;
    return;
  }

  emit('update:modelValue', modelValueAsDate)

  if (!props.autoSave) return;

  // create local time string before it is sent to database. only provide database with date part when it is a 'date'-field

  let newDateValue = modelValueAsDate.toISOString().split('T')[0];

  if (isDateTimeField()) {
    newDateValue = `${newDateValue}T${modelValueAsDate.toISOString().split('T')[1]}`;
  }

  const item = {
    rowId: props.rowId,
    databaseObjectId: props.header.databaseObjectId,
    columnName: props.header.field,
    oldValue: props.modelValue,
    newValue: newDateValue
  };

  hasError.value = false
  saving.value = true
  await tableStore.saveData(item);

  runFormatting(modelValueAsDate);

  saving.value = false;
}

</script>
<style scoped>
.right-align {
  text-align: right;
  width: 100%;
}

.verdi {
  padding: 8px;
}

.verdi.muted {
  color: var(--gray-500);
  font-style: italic;
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
}

.clear-button {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: var(--gray-500);
  font-size: 1.2rem;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.clear-button:hover {
  background-color: var(--gray-200);
  color: var(--gray-700);
}
</style>
